import { Link } from "react-router-dom";
import type { Job } from "@/lib/mockData";
import { StatusPill } from "@/components/ui/StatusPill";
import { fmtElapsed } from "@/lib/format";
import { jobStatusLabel } from "@/lib/jobStatus";
import { didRun, runSeconds } from "@/lib/usage";

interface Props {
  jobs: Job[];
}

export function RecentTable({ jobs }: Props) {
  return (
    <div className="overflow-hidden rounded-md border border-paper-line bg-paper-2">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-paper-line text-left text-[11px] uppercase tracking-wider text-ink-3">
            <Th>Status</Th>
            <Th>Workflow</Th>
            <Th>Entity</Th>
            <Th>Patient</Th>
            <Th>Started</Th>
            <Th>Duration</Th>
            <Th>Result</Th>
            <Th>Queued by</Th>
          </tr>
        </thead>
        <tbody>
          {jobs.length === 0 && (
            <tr>
              <td colSpan={8} className="p-8 text-center text-ink-3">
                Nothing matching those filters.
              </td>
            </tr>
          )}
          {jobs.map((j) => (
            <tr
              key={j.id}
              className="border-b border-paper-line last:border-0 transition-colors hover:bg-paper-tint"
            >
              <Td>
                {/* Same words as the job screen: "Waiting for a person",
                    "Supervisor in control", "Needs approval". */}
                <StatusPill status={j.status} label={jobStatusLabel(j.status, j.apiStatus)} />
              </Td>
              <Td>
                <Link to={`/jobs/${j.id}`} className="text-ink-1 hover:underline">
                  {j.workflow.name}
                </Link>
                <div className="font-mono text-[10px] text-ink-4">{j.id}</div>
              </Td>
              <Td>
                <span className="uppercase tracking-wider text-ink-3">{j.entity.name}</span>
              </Td>
              <Td>
                {j.patient ? (
                  <>
                    <div className="text-ink-1">{j.patient.name}</div>
                    <div className="font-mono text-[10px] text-ink-4">MRN {j.patient.mrn}</div>
                  </>
                ) : (
                  <span className="text-ink-4">—</span>
                )}
              </Td>
              <Td>
                <span className="font-mono text-ink-3">
                  {didRun(j) ? j.started : "Not started"}
                </span>
              </Td>
              <Td>
                <span className="font-mono text-ink-3">
                  {didRun(j) ? fmtElapsed(runSeconds(j)) : "—"}
                </span>
              </Td>
              <Td>
                <span className="text-ink-2">{j.resultSummary}</span>
              </Td>
              <Td>
                <span className="text-ink-3">{j.user.name}</span>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-3 py-2 font-medium">{children}</th>;
}

function Td({ children }: { children: React.ReactNode }) {
  return <td className="px-3 py-2 align-middle">{children}</td>;
}
